import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import createApp from "@shopify/app-bridge";
import { Redirect } from "@shopify/app-bridge/actions";
import { apiCall } from "../../helper/Common";
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from "../../config/env";

const rejectPayload = (message, redirecting = false) => ({ message, redirecting });

const getRejectMessage = (payload) => {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object") return payload.message || "Install check failed";
  return "Install check failed";
};

const isRedirectReject = (payload) => {
  if (payload && typeof payload === "object" && payload.redirecting) return true;
  const msg = getRejectMessage(payload).toLowerCase();
  return (
    msg.includes("shop not installed") ||
    msg.includes("app not installed") ||
    msg.includes("app not a installed")
  );
};

const handleRedirect = (shop, host) => {
  if (!shop) {
    console.warn("[handleRedirect] Shop parameter missing, cannot redirect");
    return false;
  }

  const redirectUrl = buildNodeApiEntryUrl(shop, host);

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const appHost = host || urlParams.get("host");

    if (appHost && SHOPIFY_API_KEY) {
      try {
        const app =
          (typeof window !== "undefined" && window.__shopifyAppBridgeInstance) ||
          createApp({
            apiKey: SHOPIFY_API_KEY,
            host: appHost,
          });
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
        return true;
      } catch (appBridgeError) {
        console.warn("[handleRedirect] App Bridge redirect failed:", appBridgeError);
      }
    }
  } catch (redirectError) {
    console.warn("[handleRedirect] App Bridge redirect failed, trying fallback:", redirectError);
  }

  try {
    if (window.top && window.top !== window) {
      window.top.location.href = redirectUrl;
    } else {
      window.location.href = redirectUrl;
    }
    return true;
  } catch (navError) {
    console.error("[handleRedirect] Navigation failed, opening in new window:", navError);
    window.open(redirectUrl, "_blank");
    return true;
  }
};

const extractShopAndHost = () => {
  const urlParams = new URLSearchParams(window.location.search);
  let shop = urlParams.get("shop");
  let host = urlParams.get("host");

  if (!shop) {
    const currentUrl = window.location.href;
    const shopMatch = currentUrl.match(/[?&]shop=([^&]+)/);
    if (shopMatch) {
      shop = decodeURIComponent(shopMatch[1]);
    }
    const hostMatch = currentUrl.match(/[?&]host=([^&]+)/);
    if (hostMatch && !host) {
      host = decodeURIComponent(hostMatch[1]);
    }
  }

  return { shop, host };
};

export const checkInstall = createAsyncThunk(
  "install/checkInstall",
  async (arg, { getState, rejectWithValue }) => {
    const forceRefresh =
      arg === true || (arg && typeof arg === "object" && arg.force === true);

    const state = getState();
    const existingInstallData = state.install.installData;
    const existingShop = state.install.shop;
    const existingHost = state.install.host;
    const isAlreadyChecked = state.install.checked;

    const { shop, host } = extractShopAndHost();

    if (
      !forceRefresh &&
      existingInstallData &&
      existingShop === shop &&
      isAlreadyChecked
    ) {
      return {
        success: true,
        shop: existingShop || shop,
        host: existingHost || host,
        data: existingInstallData,
        message: "Using cached install data",
      };
    }

    if (!shop) {
      return rejectWithValue(
        rejectPayload(
          "Shop parameter is missing. Please access the app from Shopify admin.",
          false,
        ),
      );
    }

    try {
      const response = await apiCall("install/status", "POST", { shop }, null, {
        timeout: 8000,
        sessionTokenTimeoutMs: 5000,
      });

      if (response?.success || response?.message === "success" || response?.data) {
        return {
          success: true,
          shop: shop,
          host: host,
          data: response.data || response,
          message: response.message || "Install check successful",
        };
      }

      const redirecting = handleRedirect(shop, host);
      return rejectWithValue(
        rejectPayload(response?.message || "Install API call failed", redirecting),
      );
    } catch (error) {
      let errorMessage = "App not installed";
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      const redirecting = handleRedirect(shop, host);

      return rejectWithValue(rejectPayload(errorMessage, redirecting));
    }
  },
);

const installSlice = createSlice({
  name: "install",
  initialState: {
    checked: false,
    success: false,
    loading: false,
    redirecting: false,
    error: null,
    shop: null,
    host: null,
    installData: null,
  },
  reducers: {
    resetInstall: (state) => {
      state.checked = false;
      state.success = false;
      state.loading = false;
      state.redirecting = false;
      state.error = null;
      state.shop = null;
      state.host = null;
      state.installData = null;
    },
    setShopAndHost: (state, action) => {
      state.shop = action.payload.shop;
      state.host = action.payload.host;
    },
    /** Merge clients.is_on_boarding after POST /api/client/onboarding */
    updateInstallOnboarding: (state, action) => {
      const v = String(action.payload ?? "0");
      if (state.installData?.shop_info) {
        state.installData.shop_info.is_on_boarding = v;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkInstall.pending, (state) => {
        state.loading = true;
        state.redirecting = false;
        state.error = null;
      })
      .addCase(checkInstall.fulfilled, (state, action) => {
        state.loading = false;
        state.redirecting = false;
        state.checked = true;
        state.success = true;
        state.shop = action.payload.shop;
        state.host = action.payload.host;
        state.installData = action.payload.data;
        state.error = null;
      })
      .addCase(checkInstall.rejected, (state, action) => {
        const redirecting = isRedirectReject(action.payload);
        state.redirecting = redirecting;
        state.loading = redirecting;
        state.checked = !redirecting;
        state.success = false;
        state.error = redirecting ? null : getRejectMessage(action.payload);

        const { shop, host } = extractShopAndHost();
        state.shop = state.shop || shop;
        state.host = state.host || host;
      });
  },
});

export const { resetInstall, setShopAndHost, updateInstallOnboarding } = installSlice.actions;

export const selectClientId = (state) => {
  return state.install.installData?.shop_info?.id ?? null;
};

export default installSlice.reducer;
